import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { eventInside, onEscapeKey } from "../outsideDismiss";

export type SelectOption = { value: string; label: string; disabled?: boolean };

/**
 * Themed dropdown that replaces the native <select> (whose popup list is
 * rendered by the OS and cannot be styled to match the dark app theme). Button
 * + absolutely-positioned listbox, both on opaque `bf-surface` so nothing shows
 * through. Closes on outside-click or Escape.
 */
export function Select({
  value,
  onChange,
  options,
  disabled,
  className = "",
  id,
  ariaLabel,
  title,
  placeholder = "",
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;
  className?: string;
  id?: string;
  ariaLabel?: string;
  title?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let downOutside = false;
    const onDown = (e: PointerEvent) => {
      downOutside = !eventInside(e, ref.current);
    };
    const onUp = (e: PointerEvent) => {
      if (downOutside && !eventInside(e, ref.current)) setOpen(false);
    };
    const onKey = onEscapeKey(() => setOpen(false));
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={"relative " + className}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-white/10 bg-bf-surface/60 py-2 pl-4 pr-3 text-sm font-medium text-bf-text transition hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <ChevronDown
          size={16}
          className={"shrink-0 text-bf-muted transition " + (open ? "rotate-180" : "")}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-50 mt-1 max-h-72 w-full min-w-[11rem] overflow-auto rounded-lg border border-white/10 bg-bf-surface p-1 shadow-xl shadow-black/50"
        >
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                disabled={o.disabled}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={
                  "flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-sm transition disabled:opacity-40 " +
                  (active ? "bg-bf-violet/25 text-white" : "text-bf-text hover:bg-white/5")
                }
              >
                <span className="truncate">{o.label}</span>
                {active && <Check size={14} className="shrink-0 text-bf-violet" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
